// src/components/features/brands/forms/WebsiteForm.jsx
import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  useTheme,
  alpha,
  InputAdornment,
  FormHelperText,
  Alert,
} from '@mui/material';
import {
  Language as LanguageIcon,
  ShoppingCart as EcommerceIcon,
  Article as BlogIcon,
  Web as WebIcon,
} from '@mui/icons-material';
import { FormSection } from '../../../design-system/components/forms';

/**
 * Website information form component
 * Handles website URL and features
 */
const WebsiteForm = ({ 
  brandData, 
  data, // Support for the new data prop pattern
  onChange, 
  onNestedChange,
  setFieldValue, // Added for Formik support
  errors = {},
  touched = {},
  requiredFields = []
}) => {
  const theme = useTheme();
  
  // Support both brandData and data prop patterns
  const formData = data || brandData || {};
  
  // Get website data or initialize with defaults
  const website = formData.website || {
    url: '',
    hasBlog: false,
    hasEcommerce: false
  };
  
  // Format errors for better display
  const formattedErrors = {
    ...errors,
    website: {
      ...(typeof errors.website === 'object' ? errors.website : {}),
      url: errors['website.url']
    }
  };

  // Format touched state for validation
  const formattedTouched = {
    ...touched,
    website: {
      ...(typeof touched.website === 'object' ? touched.website : {}),
      url: touched['website.url']
    }
  };
  
  // Check if website URL is required
  const isUrlRequired = requiredFields.includes('website.url');
  
  // Check for validation errors
  const hasUrlError = Boolean(formattedErrors.website?.url);
  
  // Handle website URL change with Formik support
  const handleWebsiteUrlChange = (e) => {
    if (setFieldValue) {
      setFieldValue('website.url', e.target.value);
    } else if (onNestedChange) {
      onNestedChange('website', 'url', e.target.value);
    }
  };
  
  // Handle blog switch change with Formik support
  const handleBlogChange = (e) => {
    if (setFieldValue) {
      setFieldValue('website.hasBlog', e.target.checked);
    } else if (onNestedChange) {
      onNestedChange('website', 'hasBlog', e.target.checked);
    }
  };
  
  // Handle ecommerce switch change with Formik support
  const handleEcommerceChange = (e) => {
    if (setFieldValue) {
      setFieldValue('website.hasEcommerce', e.target.checked);
    } else if (onNestedChange) {
      onNestedChange('website', 'hasEcommerce', e.target.checked);
    }
  };
  
  return (
    <FormSection
      title="Website Information"
      description="Provide details about the brand's website and online presence"
      icon={<WebIcon />}
    >
      {/* Display error alert if URL validation fails */}
      {hasUrlError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {formattedErrors.website?.url}
        </Alert>
      )}
      
      {/* Website URL */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Website URL"
            value={website.url || ''}
            onChange={handleWebsiteUrlChange}
            fullWidth
            placeholder="https://brand-website.com"
            required={isUrlRequired}
            error={hasUrlError && formattedTouched.website?.url}
            helperText={
              (formattedTouched.website?.url && formattedErrors.website?.url) || 
              "Enter the brand's primary website URL"
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LanguageIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {/* Website Features */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Website Features
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={website.hasBlog || false}
                onChange={handleBlogChange}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BlogIcon sx={{ mr: 1, color: website.hasBlog ? theme.palette.primary.main : theme.palette.text.disabled }} />
                Website has a blog
              </Box>
            }
          />
          <FormHelperText>Content publishing capability and article sections</FormHelperText>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={website.hasEcommerce || false}
                onChange={handleEcommerceChange}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EcommerceIcon sx={{ mr: 1, color: website.hasEcommerce ? theme.palette.primary.main : theme.palette.text.disabled }} />
                Website has e-commerce functionality
              </Box>
            }
          />
          <FormHelperText>Online shopping and product purchasing features</FormHelperText>
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default WebsiteForm;